import { Link } from '@remix-run/react'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/shared/lib/misc.js'
import { Ripple } from '../../utils/ripple/Ripple.tsx'

const buttonVariants = cva(
  `
    inline-flex items-center justify-center gap-3 rounded-[4px] font-bold
    ring-offset-cards transition-all duration-200

    disabled:pointer-events-none disabled:opacity-50

    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    focus-visible:ring-ring
  `,
  {
    variants: {
      variant: {
        primary: `
          border border-[rgba(255,255,255,0.14)] bg-primary-500 text-white

          hover:border-primary-600 hover:bg-primary-600 hover:text-white
        `,
        //   active:bg-primary-700
        secondary: `
          bg-secondary-600 text-white

          hover:bg-secondary
        `,
        // for dashboard buttons
        neutral: `
          bg-purple-800 text-white

          hover:bg-purple-600
        `,
        // for dashboard inputs like datepicker or pagination
        outlineInput: `
          border border-white bg-transparent

          hover:bg-[rgba(255,255,255,0.10)]
        `,
        //   active:bg-gray-100

        //   focus-visible:ring-0

        //   focus:border-2 focus:border-foreground

        // admin product customization delete
        danger: `
          bg-danger text-white

          hover:bg-badge-red-foreground
        `,
        // admin product customization delete
        dangerOutline: `
          border border-danger text-danger

          hover:bg-gray-100
        `,
        // date picker
        ghost: `
          text-gray-600

          hover:bg-gray-100
        `,
        gray: `
          border border-purple-300 bg-gray-50 text-foreground

          active:bg-gray-200

          hover:bg-gray-100
        `,
        semiTransparent: `
          border border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.07)]
          text-white

          active:bg-[rgba(255,255,255,0.24)]

          hover:bg-[rgba(255,255,255,0.14)]
        `,
        text: `cursor-pointer text-blue-600 underline`,
        banner: `
          rounded-[2px] border border-[1px] border-[rgba(255,255,255,0.30)]
          !px-2 py-[12px] text-white

          active:bg-[rgba(255,255,255,0.24)]

          hover:bg-[rgba(255,255,255,0.14)]
        `,
      },
      size: {
        '28px': 'h-7 px-1.5',
        '32px': 'h-8 px-2',
        '36px': 'h-9 px-3',
        '44px': 'h-11 px-4',
        '48px': 'h-12 px-8',
        '64px': 'h-16 px-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: '44px',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  enableRipple?: boolean
  as?: 'button' | 'div' | 'link'
  to?: string
}

const Button = React.forwardRef<
  HTMLButtonElement | HTMLDivElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      enableRipple,
      children,
      variant,
      size,
      as = 'button',
      to,
      ...props
    },
    ref,
  ) => {
    const Component = as === 'link' ? Link : as

    if (as === 'link' && !to) {
      console.warn('Button: "to" prop is required when "as" is set to "link"')
    }

    return (
      <Component
        className={cn(
          'relative cursor-pointer overflow-hidden',
          buttonVariants({ variant, size, className }),
        )}
        ref={ref as any}
        {...(as === 'link' ? { to } : {})}
        {...(props as any)}
      >
        {children}
        {enableRipple && <Ripple />}
      </Component>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
